import type { ProjectUpsertPayload } from '../../services/commercialProjectsApi';
import { FieldLabel } from '../ui/FieldLabel';
import { PROJECT_STATUS_LABELS } from '../../utils/projectFormUtils';
import { projectFieldHelp } from './projectFieldHelp';

type ProjectFormFieldsProps = {
  form: ProjectUpsertPayload;
  canWrite: boolean;
  onChange: (next: ProjectUpsertPayload) => void;
};

export function ProjectFormFields({ form, canWrite, onChange }: ProjectFormFieldsProps) {
  return (
    <div className="space-y-4">
      <div className="grid gap-3 md:grid-cols-2">
        <label className="erp-label">
          <FieldLabel {...projectFieldHelp.name} required>
            Nome
          </FieldLabel>
          <input
            className="erp-input mt-1"
            required
            value={form.name}
            onChange={(event) => onChange({ ...form, name: event.target.value })}
            disabled={!canWrite}
          />
        </label>
        <label className="erp-label">
          <FieldLabel {...projectFieldHelp.code}>Código</FieldLabel>
          <input
            className="erp-input mt-1"
            value={form.code ?? ''}
            onChange={(event) => onChange({ ...form, code: event.target.value })}
            disabled={!canWrite}
          />
        </label>
        <label className="erp-label">
          <FieldLabel {...projectFieldHelp.status} required>
            Status
          </FieldLabel>
          <select
            className="erp-input mt-1"
            value={form.status}
            onChange={(event) => onChange({ ...form, status: event.target.value })}
            disabled={!canWrite}
          >
            {Object.entries(PROJECT_STATUS_LABELS).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </label>
        <label className="erp-label">
          <FieldLabel {...projectFieldHelp.lifecycleModel} required>
            Ciclo de vida
          </FieldLabel>
          <select
            className="erp-input mt-1"
            value={form.lifecycleModel}
            onChange={(event) => onChange({ ...form, lifecycleModel: event.target.value })}
            disabled={!canWrite}
          >
            <option value="Waterfall">Cascata</option>
            <option value="Iterative">Iterativo</option>
            <option value="Incremental">Incremental</option>
            <option value="Agile">Ágil</option>
            <option value="Hybrid">Híbrido</option>
            <option value="Custom">Customizado</option>
          </select>
        </label>
      </div>

      <label className="erp-label">
        <FieldLabel {...projectFieldHelp.description}>Descrição</FieldLabel>
        <textarea
          className="erp-input mt-1"
          rows={2}
          value={form.description ?? ''}
          onChange={(event) => onChange({ ...form, description: event.target.value })}
          disabled={!canWrite}
        />
      </label>

      <label className="erp-label">
        <FieldLabel {...projectFieldHelp.scope}>Escopo</FieldLabel>
        <textarea
          className="erp-input mt-1"
          rows={3}
          value={form.scopeStatement ?? ''}
          onChange={(event) => onChange({ ...form, scopeStatement: event.target.value })}
          disabled={!canWrite}
        />
      </label>
      <label className="erp-label">
        <FieldLabel {...projectFieldHelp.workProducts}>Produtos de trabalho</FieldLabel>
        <textarea
          className="erp-input mt-1"
          rows={2}
          value={form.workProducts ?? ''}
          onChange={(event) => onChange({ ...form, workProducts: event.target.value })}
          disabled={!canWrite}
        />
      </label>

      <div className="grid gap-3 md:grid-cols-3">
        <label className="erp-label">
          <FieldLabel {...projectFieldHelp.sizeEstimate}>Tamanho</FieldLabel>
          <input
            className="erp-input mt-1"
            type="number"
            step="0.01"
            value={form.sizeEstimate ?? ''}
            onChange={(event) =>
              onChange({
                ...form,
                sizeEstimate: event.target.value ? Number(event.target.value) : null,
              })
            }
            disabled={!canWrite}
          />
        </label>
        <label className="erp-label">
          <FieldLabel {...projectFieldHelp.sizeUnit}>Unidade</FieldLabel>
          <input
            className="erp-input mt-1"
            value={form.sizeUnit ?? ''}
            onChange={(event) => onChange({ ...form, sizeUnit: event.target.value })}
            disabled={!canWrite}
          />
        </label>
        <label className="erp-label">
          <FieldLabel {...projectFieldHelp.effortHours}>Esforço (h)</FieldLabel>
          <input
            className="erp-input mt-1"
            type="number"
            step="0.01"
            value={form.effortEstimateHours ?? ''}
            onChange={(event) =>
              onChange({
                ...form,
                effortEstimateHours: event.target.value ? Number(event.target.value) : null,
              })
            }
            disabled={!canWrite}
          />
        </label>
        <label className="erp-label">
          <FieldLabel {...projectFieldHelp.costEstimate}>Custo estimado</FieldLabel>
          <input
            className="erp-input mt-1"
            type="number"
            step="0.01"
            value={form.costEstimate ?? ''}
            onChange={(event) =>
              onChange({
                ...form,
                costEstimate: event.target.value ? Number(event.target.value) : null,
              })
            }
            disabled={!canWrite}
          />
        </label>
        <label className="erp-label">
          <FieldLabel {...projectFieldHelp.budget}>Orçamento</FieldLabel>
          <input
            className="erp-input mt-1"
            type="number"
            step="0.01"
            value={form.budgetAmount ?? ''}
            onChange={(event) =>
              onChange({
                ...form,
                budgetAmount: event.target.value ? Number(event.target.value) : null,
              })
            }
            disabled={!canWrite}
          />
        </label>
        <label className="erp-label">
          <FieldLabel {...projectFieldHelp.plannedStart}>Início planejado</FieldLabel>
          <input
            className="erp-input mt-1"
            type="date"
            value={form.plannedStartDateUtc ?? ''}
            onChange={(event) => onChange({ ...form, plannedStartDateUtc: event.target.value })}
            disabled={!canWrite}
          />
        </label>
        <label className="erp-label">
          <FieldLabel {...projectFieldHelp.plannedEnd}>Fim planejado</FieldLabel>
          <input
            className="erp-input mt-1"
            type="date"
            value={form.plannedEndDateUtc ?? ''}
            onChange={(event) => onChange({ ...form, plannedEndDateUtc: event.target.value })}
            disabled={!canWrite}
          />
        </label>
      </div>

      <label className="erp-label">
        <FieldLabel {...projectFieldHelp.lifecyclePhases}>Fases do ciclo de vida</FieldLabel>
        <textarea
          className="erp-input mt-1"
          rows={2}
          value={form.lifecyclePhases ?? ''}
          onChange={(event) => onChange({ ...form, lifecyclePhases: event.target.value })}
          disabled={!canWrite}
        />
      </label>
      <label className="erp-label">
        <FieldLabel {...projectFieldHelp.resourcesPlan}>Recursos e infraestrutura</FieldLabel>
        <textarea
          className="erp-input mt-1"
          rows={2}
          value={form.resourcesPlan ?? ''}
          onChange={(event) => onChange({ ...form, resourcesPlan: event.target.value })}
          disabled={!canWrite}
        />
      </label>
      <label className="erp-label">
        <FieldLabel {...projectFieldHelp.feasibility}>Viabilidade</FieldLabel>
        <textarea
          className="erp-input mt-1"
          rows={2}
          value={form.feasibilityAssessment ?? ''}
          onChange={(event) => onChange({ ...form, feasibilityAssessment: event.target.value })}
          disabled={!canWrite}
        />
      </label>
      <label className="erp-label-inline text-sm">
        <input
          type="checkbox"
          checked={Boolean(form.stakeholderCommitmentObtained)}
          onChange={(event) =>
            onChange({ ...form, stakeholderCommitmentObtained: event.target.checked })
          }
          disabled={!canWrite}
        />
        <FieldLabel {...projectFieldHelp.stakeholderCommitment}>
          Compromisso dos interessados obtido
        </FieldLabel>
      </label>
      <label className="erp-label">
        <FieldLabel {...projectFieldHelp.monitoring}>Monitoramento</FieldLabel>
        <textarea
          className="erp-input mt-1"
          rows={2}
          value={form.monitoringNotes ?? ''}
          onChange={(event) => onChange({ ...form, monitoringNotes: event.target.value })}
          disabled={!canWrite}
        />
      </label>
      <label className="erp-label">
        <FieldLabel {...projectFieldHelp.notes}>Observações</FieldLabel>
        <textarea
          className="erp-input mt-1"
          rows={2}
          value={form.notes ?? ''}
          onChange={(event) => onChange({ ...form, notes: event.target.value })}
          disabled={!canWrite}
        />
      </label>
    </div>
  );
}
